import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
} from "../../ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { useState, useRef, useCallback, useEffect } from "react";
import { Skeleton } from "../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { ResultHeading } from "./ResultHeading";
import { Badge } from "../../ui/badge";

export type Option = Record<"value" | "label", string> & Record<string, string>;

export type Options = {
  group: string;
  data: Option[];
};

type MultiSelectAutoCompleteProps = {
  options: Options[];
  emptyMessage: string;
  value?: Option[];
  onValueChange?: (value: Option[]) => void;
  onBlur: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onInputValueChange: (value: any) => void;
  inputValue: string;
  className: string;
  parentWidth?: number;
  city?: any;
};

export const MultiSelectAutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  onInputValueChange,
  inputValue,
  className,
  onBlur,
  parentWidth,
  city,
}: MultiSelectAutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option[]>(value ? value : []); //(value as Option);
  const [displayBadges, setDisplayBadges] = useState<boolean>(true);
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(false);

  const handleBlur = useCallback(() => {
    setOpen(false);
    setDisplayBadges(true);
    onBlur();
    setShowPlaceholder(false);
  }, [selected]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setSelected((prevSelected) => {
        const newSelected = prevSelected?.some(
          (option) => option.value === selectedOption.value
        )
          ? prevSelected.filter(
              (option) => option.value !== selectedOption.value
            ) // Remove option if already selected
          : [...prevSelected, selectedOption];

        if (onValueChange) {
          onValueChange(newSelected as Option[]);
        }

        return newSelected;
      });
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange]
  );

  const handleItemRemove = useCallback((itemToRemove: Option) => {
    setSelected((prevSelected) => {
      const updatedSelected = prevSelected.filter(
        (item) => item.value !== itemToRemove.value
      );
      if (onValueChange) {
        onValueChange(updatedSelected as Option[]); // Call onValueChange with the updated array
      }
      return updatedSelected;
    });
  }, []);

  const handleAddClick = () => {
    setDisplayBadges(false);
    inputRef.current?.focus(); // Focus the input field
  };

  useEffect(() => {
    setSelected(value ? value : []);
  }, [value]);

  return (
    <CommandPrimitive shouldFilter={false} className="w-full">
      <div className="relative">
        {displayBadges && (
          <div className="absolute overflow-hidden z-10 top-1.5 flex space-x-2">
            {selected.slice(0, 1).map((item, index) => (
              <Badge
                key={index}
                className="flex py-[3px] bg-brand-bgBlack font-normal items-center space-x-1 capitalize"
              >
                <span>{item.label}</span>
                <button onClick={() => handleItemRemove(item)} className="ml-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </Badge>
            ))}
            {selected.length > 1 && (
              <Badge className="flex py-[3px] bg-brand-bgBlack font-normal items-center">
                +{selected.length - 1} more
              </Badge>
            )}
            {selected.length > 0 && (
              <Badge
                onClick={() => handleAddClick()} // Enable input on click
                className="cursor-pointer bg-white border-[0.5px] font-normal border-black text-brand-textBlack flex py-[3px] items-center" // Add cursor pointer for better UX
              >
                +Add
              </Badge>
            )}
          </div>
        )}
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={onInputValueChange}
          onBlur={handleBlur}
          onFocus={() => {
            setDisplayBadges(false);
            setOpen(true);
            setShowPlaceholder(true);
          }}
          placeholder={
            showPlaceholder || !(selected.length > 0) ? placeholder : ""
          }
          disabled={disabled}
          className={cn(className)}
        />
      </div>
      <div className="relative">
        <div
          className={cn(
            `animate-in fade-in-0 zoom-in-95 absolute -right-9 top-1 z-50 rounded-xl bg-white outline-none`,
            isOpen ? "block" : "hidden"
          )}
          style={{ width: parentWidth }}
        >
          <CommandList
            className="rounded-lg ring-1 ring-slate-200"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {selected.length > 0 && !isLoading ? (
              <div key="selected_items">
                <div
                  key={"selected_item_badges"}
                  className={cn(
                    "flex flex-wrap w-full text-web-body-lg text-brand-textBlack capitalize pt-[19px] px-[28px] items-center"
                  )}
                >
                  <div className="flex space-x-2 flex-wrap">
                    {selected.map((item, index) => (
                      <Badge
                        key={index}
                        className="flex py-[3px] bg-brand-bgBlack font-normal items-center space-x-1 mb-1"
                      >
                        <span>{item.label}</span>
                        <button
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleItemRemove(item);
                          }}
                          className="ml-1"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {options.length > 0 && !isLoading
              ? options.map((option: Options) => {
                  return (
                    <CommandGroup
                      key={option.group}
                      heading={
                        <ResultHeading city={city} groupName={option.group} />
                      }
                    >
                      {option?.data?.map((item: Option) => {
                        // const isSelected = selected?.some(
                        //   (selectedItem) => selectedItem.value === item.value
                        // ); // Check if item is selected
                        return (
                          <CommandItem
                            key={item.value}
                            value={item.label}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onSelect={() => handleSelectOption(item)}
                            className={cn(
                              "flex w-full text-web-body-lg hover:bg-gray-50 text-brand-textBlack capitalize pt-[19px] pb-[18px] px-[28px] items-center"
                              //   isSelected &&
                              //     "bg-brand-lightOrange hover:bg-brand-lightOrange"
                            )}
                          >
                            {item.label}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })
              : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
